import { type UseMutationOptions, useMutation } from '@tanstack/react-query';
import { useCallback, useRef } from 'react';

import { fetchAccountPortalbackend } from './account-portal.helpers';
import { ACCOUNT_PORTAL_BACKEND_ROOT } from './auth';

export interface ContactSalesParams {
  email: string | null | undefined;
  trialUpgrade: string;
}

export type ContactSalesMutationOptions = Omit<
  UseMutationOptions<Response, Error, ContactSalesParams, unknown>,
  'mutationKey' | 'mutationFn'
>;

export function useContactSalesMutation(options?: ContactSalesMutationOptions) {
  // Every caller gates on a once-a-day `contacted-sales` localStorage stamp,
  // but that stamp is only written in `onSuccess` — so two clicks landing
  // before the first response both read an empty value and both send a sales
  // email. `isPending` can't close that window either, since it only reaches
  // the DOM a render later; a ref updates synchronously.
  const inFlight = useRef(false);

  const mutation = useMutation({
    ...options,
    mutationKey: ['auth', 'contact-sales'],
    mutationFn: async (params: ContactSalesParams) => {
      const email = params.email;
      if (
        email === null ||
        email === undefined ||
        String(email).trim() === ''
      ) {
        throw new Error('Contact sales requires an email address');
      }

      const url = `${ACCOUNT_PORTAL_BACKEND_ROOT}/api/users/hubspot/contactsales`;

      if (url.includes('/api/api')) {
        const errorMsg = `Contact sales URL contains doubled /api path: ${url}`;
        console.error(errorMsg);
        throw new Error(errorMsg);
      }

      const response = await fetchAccountPortalbackend(url, {
        method: 'POST',
        body: JSON.stringify(params),
      });

      if (!response.ok) {
        throw new Error(
          `Contact sales request failed with status ${response.status}`
        );
      }

      return response;
    },
  });

  // Read the mutation through a ref rather than a dep: TanStack rebuilds the
  // result object every render, so depending on it would hand every consumer a
  // new `mutate` identity each render where the raw one is stable.
  const mutationRef = useRef(mutation);
  mutationRef.current = mutation;

  // The ref is set and cleared entirely within `mutate`, chaining the caller's
  // own onSettled. A hook-level onSettled would also fire for `mutateAsync`
  // and could clear the flag out from under an in-flight `mutate`.
  // `mutateAsync` stays raw so its contract (resolves the Response, or throws)
  // is unchanged; no caller uses it, and wrapping it could only be done by
  // inventing a return value for a dropped call.
  const mutate: typeof mutation.mutate = useCallback((variables, options) => {
    if (inFlight.current) {
      return;
    }
    inFlight.current = true;
    mutationRef.current.mutate(variables, {
      ...options,
      onSettled: (...args) => {
        inFlight.current = false;
        options?.onSettled?.(...args);
      },
    });
  }, []);

  return { ...mutation, mutate };
}
